import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import MessagesShimmer from "./Messages/MessagesShimmer";

interface PlaygroundShimmerProps {
  className?: string;
  fullWidth?: boolean;
}

export const PlaygroundShimmer = ({
  className,
  fullWidth = false,
}: PlaygroundShimmerProps) => {
  return (
    <div className={cn("flex h-full w-full flex-col md:flex-row", className)}>
      {/* Messages area */}
      <div
        className={cn(
          "flex h-full flex-col",
          fullWidth ? "w-full" : "w-full md:w-2/3",
        )}
      >
        {/* Header */}
        <div className="h-[65px] w-full border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-32" />
            <div className="flex space-x-2">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-9 rounded-md" />
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3">
          <MessagesShimmer count={3} />
        </div>

        {/* Footer */}
        <div className="border-t p-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-2 overflow-x-auto">
              <Skeleton className="h-8 w-24 rounded-full" />
              <Skeleton className="h-8 w-24 rounded-full" />
            </div>
            <div className="flex space-x-2">
              <Skeleton className="h-9 w-10 rounded-md" />
              <Skeleton className="h-9 w-36" />
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar (right side) */}
      {!fullWidth && (
        <div className="h-full w-full border-l md:w-1/3">
          {/* Control bar */}
          <div className="h-[65px] w-full border-b px-4 py-3">
            <div className="flex items-center justify-end space-x-2">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-28" />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4 p-3">
            {/* Test cases widget */}
            <div className="rounded-lg border p-3 shadow-sm">
              <div className="flex items-center justify-between pb-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-8 rounded-full" />
                </div>
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
              <div className="flex items-center space-x-2 overflow-x-auto pb-2">
                <Skeleton className="h-32 w-48 flex-shrink-0 rounded-md" />
                <Skeleton className="h-32 w-48 flex-shrink-0 rounded-md" />
              </div>
            </div>

            {/* Outputs widget */}
            <div className="rounded-lg border p-3 shadow-sm">
              <div className="flex items-center justify-between pb-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-8 rounded-full" />
                </div>
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
              <div className="space-y-3">
                <div className="overflow-hidden rounded-lg border">
                  <div className="border-b bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-5 w-5 rounded-full" />
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  </div>
                  <div className="p-3">
                    <Skeleton className="h-32 w-full rounded-md" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaygroundShimmer;
